import React, { useState } from 'react';
import Paper from '@mui/material/Paper';
import Link from '@mui/material/Link';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Modal from '@mui/material/Modal/Modal';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import CloseIcon from '@mui/icons-material/Close';

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    height: '90%',
    p: 4,
};

export function ProjectImageList(props) {
    const { images } = props;
    const [ modalImage, setModalImage ] = useState(null);

    const imgModalStyle = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translateX(-50%) translateY(-50%)',
        maxWidth: '100%',
        maxHeight: '100%',
        verticalAlign: 'middle' as 'middle',
        objectFit: 'contain' as 'contain',
    };

    return <ImageList
        variant='quilted'
    >
        {Object.keys(images).map(key => <ImageListItem key={`img-${key}`} style={{cursor: 'pointer'}}>
            <img src={`../../../${images[key]}`} alt={key} loading='lazy' onClick={() => { setModalImage(images[key]); }} />
            <Modal
                open={modalImage !== null}
                onClose={() => setModalImage(null)}
            >
                <Box sx={modalStyle}>
                    <img src={`../../../${modalImage}`} alt={key} loading='lazy' style={imgModalStyle} />
                   <Fab
                        onClick={() => setModalImage(null)}
                        style={{position: 'absolute', top: '24px', right: '24px'}}
                    >
                        <CloseIcon />
                    </Fab> 
                </Box>
            </Modal>
        </ImageListItem>)}
    </ImageList>;
}

export default function Projects() {
    return <Paper elevation={3} className='section-container' id='projects'>
        <h2>Projects</h2>
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography><b>Matter Energy Information</b> <i>A Rust library, API, and Godot extension for generating semi-realistic galaxy data for use in space simulations.</i></Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Typography>
                    A Rust library, leveraging compound density functions and more modeling to generate a galaxy with a realistic number of stars, but not store anything on disk.
                    The API provides endpoints to query galaxy data and the Godot extension enables real-time rendering with customizable parameters.
                    This project is meant to enable others to create realistic space simulations with random realistically sized galaxies.
                    When you load, you get a sampling of stars from to form the general galactic structure, then as you move around, you can query the exact stars nearby.
                    You can also query a specific star system, since each "star" is actually a potential collection of star-like bodies as well as planets, moons, and other celestial objects.
                    This data is a starting point for generating detailed stellar and planetary information on-demand, allowing for dynamic exploration of celestial bodies in real-time.
                    Seed 0 is always the Milky Way galaxy, but other seeds can generate globular clusters or spiral galaxies.
                </Typography>
                <ProjectImageList images={ {
                    'mei-galaxy': 'mei-galaxy.png',
                    'mei-system': 'mei-system.png',
                } } />
            </AccordionDetails>
        </Accordion>
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography><b>Budgie</b> <i>Eventually Open-sourced zero-based-budgeting app</i></Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Typography>
                    A zero-based budgeting app designed for all us normies and not the rich. It's a work in progress and but progress is made steadily.
                    This has gone through a few iterations. I started it as a website, but then I decided to make it a desktop app using Qt and Python,
                    because of data security and privacy concerns. This way you can house your own data.
                </Typography>
                <ProjectImageList images={ {
                    'budgieqtpy-accounts': 'budgieqtpy-accounts.png',
                    'budgieqtpy-extrapolate': 'budgieqtpy-extrapolate.png',
                    'budgie-complex': 'budgie-complex.png',
                    'budgie-budget': 'budgie-budget.png',
                    'budgie-calendar': 'budgie-calendar.png',
                    'budgie-mark': 'budgie-mark.png',
                } } />
            </AccordionDetails>
        </Accordion>
        <Accordion>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-wwda-content"
                id="panel-wwda-header"
            >
                <Typography><b>Well, We're Doomed Anyway</b> <i>(2D/3D Game, Godot, Custom Engine, TypeScript, WebGL)</i></Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Typography>Starting with just the goal of using a strategy game as a way of learning more TypeScript and optimizing WebGL rendering, I ended up making most of a game, so I’m trying to finish this now. The repo is private, but if you send me a GitHub login, I’ll gladly share the code. It isn’t tested well as it’s just a side project, but I’m happy with how it’s going. The renderer does basic scene sorting/graphing because I actually had some slowdowns with the graphics layer, especially with particle systems, etc.</Typography>
                <Typography>UPDATE: After some work I decided to port the game to Godot to learn more modern game development. It's now a 3D/2D game with all sorts of new features.</Typography>
                <ProjectImageList images={ {
                    'wwdh-characterselect': 'wwdh-characterselect.png',
                    'wwdh-overworld': 'wwdh-overworld.png',
                    'wwdh-beach': 'wwdh-beach.png',
                    'wwdh-forest': 'wwdh-forest.png',
                    'wwda-title': 'wwda-title.png',
                    'wwda-camp': 'wwda-camp.png',
                    'wwda-battle': 'wwda-battle.png',
                } } />
            </AccordionDetails>
        </Accordion>
        <Accordion>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-inhumanus-content"
                id="panel-inhumanus-header"
            >
                <Typography><b>Inhumanus</b> <i>(VR Game, SteamVR, Unity)</i></Typography>
            </AccordionSummary>
            <AccordionDetails>
                <div><Link href='https://store.steampowered.com/app/767970/Inhumanus/' target='none'>Steam</Link></div>
                <Typography>With another friend, we made a VR boomer-shooter with coop. Using Unity and SteamVR I implemented all of the AI, gun-handling, a few levels, and other random bits. It’s not a great game, but it was a fun project, and my first full side-game release.</Typography>
                <ProjectImageList images={ {
                    'inhum-title': 'inhum-title.jpg',
                    'inhum-menu': 'inhum-menu.jpg',
                    'inhum-bh': 'inhum-bh.jpg',
                    'inhum-bot': 'inhum-bot.jpg',
                    'inhum-ship': 'inhum-ship.jpg',
                    'inhum-vents': 'inhum-vents.jpg',
                } } />
            </AccordionDetails>
        </Accordion>
        <Accordion>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-planets-content"
                id="panel-planets-header"
            >
                <Typography><b>Past Game Engine and Planet Generator Work</b> <i>(3D, Custom Engine, GLSL, Procedural Generation)</i></Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Typography>
                    I spent quite a few years using the excuse of making my own C++/OpenGL game engine, capable of generating planets and doing deferred rendering, to learn a whole slew of things. Here's some screenshots!
                    Sometimes I contribute to other projects, such as when this gfx lib in Rust was started, I made a deferred demo for it. It has since been deprecated and taken over, but I loved doing it, and things like it. <Link href='https://github.com/glium/glium/blob/master/examples/deferred.rs' target='_blank'>Github Source</Link>
                </Typography>
                <ProjectImageList images={ {
                    'planet1': 'planet1.jpg',
                    'planet2': 'planet2.jpg',
                    'planet3': 'planet3.jpg',
                    'planet5': 'planet5.jpg',
                    'deferred-cube': 'deferred-cube.jpg',
                    'deferred-jet': 'deferred-jet-bloom-shadows.jpg',
                }} />
            </AccordionDetails>
        </Accordion>
    </Paper>;
}
